/* eslint-disable max-len */
// src/components/Dashboard.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot, Timestamp } from "firebase/firestore";
import { db, auth } from '../../firebaseConfig';
import { Link, useNavigate } from 'react-router-dom';
import {
    FiPlus, FiGrid, FiSearch, FiArrowRight, FiAlertCircle, FiCheckCircle,
    FiCalendar, FiEdit2, FiZap, FiChevronLeft, FiChevronRight,
    FiEye, FiImage, FiLoader, FiLayers, FiFacebook, FiInstagram,
    FiHeart, FiMessageCircle
} from 'react-icons/fi';
import PostSummaryModal from '../common/PostSummaryModal';
import GettingStarted from './GettingStarted';
import { getPlatformDisplay } from '../shared/utils/platformHelpers';
import packageJson from '../../../package.json';

const APP_VERSION = packageJson.version;

// --- Helper: Format Time ---
const formatTime = (date) => {
    if (!date) return '--:--';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// --- Compact Project Card for Selection ---
const SelectableProjectCard = ({ project, isSelected, onClick }) => {
    const platformDisplay = getPlatformDisplay(project);
    const PlatformIcon = platformDisplay.Icon;
    
    return (
        <button
            onClick={onClick}
            className={`text-left w-full p-6 rounded-[2rem] border transition-all duration-300 group relative overflow-hidden ${
                isSelected 
                    ? 'border-slate-blue bg-slate-blue/10 shadow-glow' 
                    : 'border-white/5 bg-warm-gray/10 hover:border-slate-blue/30 hover:bg-warm-gray/20'
            }`}
        >
            <div className="flex items-center gap-5">
                {project.coverImageUrl ? (
                    <img
                        src={project.coverImageUrl}
                        alt={project.projectName}
                        className="w-16 h-16 rounded-2xl object-cover flex-shrink-0 border border-white/5 shadow-md"
                    />
                ) : (
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-white/5 to-white/10 flex items-center justify-center flex-shrink-0 border border-white/5 group-hover:scale-105 transition-transform">
                        <PlatformIcon className={`h-7 w-7 ${platformDisplay.color}`} />
                    </div>
                )}
                <div className="min-w-0 flex-1">
                    <p className={`font-bold truncate text-lg uppercase tracking-tight transition-colors ${isSelected ? 'text-slate-blue' : 'text-cream group-hover:text-white'}`}>
                        {project.projectName}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                        <span className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider ${platformDisplay.color}`}>
                            <PlatformIcon size={12} />
                            {platformDisplay.name}
                        </span>
                        <span className="text-cream/20">•</span>
                        <span className="text-xs text-cream/40 truncate font-medium">{project.industry}</span>
                    </div>
                </div>
            </div>
        </button>
    );
};

// --- Attention Alert Item ---
const AttentionItem = ({ icon, iconColor, bgColor, borderColor, title, subtitle, actions }) => (
    <div className={`p-5 rounded-3xl ${bgColor} border ${borderColor} flex items-start gap-4 transition-all hover:translate-x-1`}>
        <div className={`p-3 rounded-2xl ${iconColor} bg-white/10`}>
            {icon}
        </div>
        <div className="flex-1 min-w-0 pt-1">
            <p className="text-sm font-bold text-cream uppercase tracking-wide">{title}</p>
            {subtitle && <p className="text-xs text-cream/50 mt-1 line-clamp-1">{subtitle}</p>}
        </div>
        {actions && (
            <div className="flex gap-2 flex-shrink-0 self-center">
                {actions}
            </div>
        )}
    </div>
);

// --- Quick Action Button ---
const QuickActionButton = ({ icon, label, onClick, disabled }) => (
    <button
        onClick={onClick}
        disabled={disabled}
        className={`w-full flex items-center gap-4 p-5 rounded-3xl border transition-all duration-200 group ${
            disabled 
                ? 'border-white/5 bg-white/5 text-cream/20 cursor-not-allowed' 
                : 'border-white/10 bg-warm-gray/20 hover:bg-slate-blue hover:border-slate-blue text-cream hover:text-warm-dark shadow-sm hover:shadow-glow'
        }`}
    >
        <div className={`p-3 rounded-2xl ${disabled ? 'bg-white/5' : 'bg-white/10 group-hover:bg-warm-dark/20 transition-colors'}`}>
             {React.cloneElement(icon, { className: `h-5 w-5 ${disabled ? 'text-cream/20' : 'text-slate-blue group-hover:text-warm-dark'}` })}
        </div>
        <span className="font-bold text-sm uppercase tracking-wide">{label}</span>
        {!disabled && <FiArrowRight className="ml-auto h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity" />}
    </button>
);

// --- Component: Schedule Post Row (with Toggle & Pills) ---
const SchedulePostRow = ({ post, onExpand, isHovered }) => {
    // Platform Toggle State (Local)
    // Default to 'facebook', but if not enabled, check instagram
    const [viewPlatform, setViewPlatform] = useState('facebook');
    
    // Status Pills Logic
    const getStatusPill = () => {
        if (post.status === 'failed') return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-soft-coral/10 text-soft-coral border border-soft-coral/20">FAILED</span>;
        if (post.status === 'posted') return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-pastel-green-bg/10 text-pastel-green border border-pastel-green/20">POSTED</span>;
        if (post.status === 'scheduled' && post.visualStatus === 'ready') return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-blue/10 text-slate-blue border border-slate-blue/20">SCHEDULED</span>;
        if (post.status === 'posting') return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-pale-lilac/10 text-pale-lilac border border-pale-lilac/20">POSTING...</span>;
        if (post.status === 'cancelled') return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/5 text-white/30 border border-white/10 line-through decoration-white/30">CANCELLED</span>;
        return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-cream/10 text-cream/40 border border-white/10">DRAFT</span>;
    };

    const content = viewPlatform === 'facebook' ? post.content?.facebook : post.content?.instagram;
    const contentPreview = (content || post.prompt || 'No content generated').slice(0, 50);

    return (
        <div 
            className={`flex items-start gap-5 p-5 bg-warm-gray/10 backdrop-blur-md rounded-[2rem] border transition-all duration-200 group cursor-pointer hover:bg-warm-gray/20 ${
                isHovered 
                    ? 'border-slate-blue ring-1 ring-slate-blue/50 shadow-glow-sm' 
                    : 'border-white/5 hover:border-white/10'
            }`}
            onClick={(e) => {
                // Prevent modal open when clicking toggles
                if(e.target.closest('.platform-toggle')) return;
                onExpand(post);
            }}
        >
            {/* Time Column */}
            <div className="flex flex-col items-center justify-center w-16 h-16 bg-black/20 rounded-2xl border border-white/5 flex-shrink-0 mt-1">
                {(() => {
                    const displayTime = post.scheduledTime?.toDate?.() || post.postedAt?.toDate?.();
                    return displayTime ? (
                        <>
                            <span className="text-sm font-bold text-white uppercase tracking-widest leading-none">
                                {displayTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }).replace(/AM|PM/, '')}
                            </span>
                            <span className="text-[10px] font-black text-slate-blue uppercase tracking-widest leading-none mt-1">
                                {displayTime.toLocaleTimeString('en-US', { hour12: true }).slice(-2)}
                            </span>
                        </>
                    ) : (
                        <span className="text-sm font-bold text-white leading-none">--</span>
                    );
                })()}
            </div>

            {/* Content Column */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                        {getStatusPill()}
                        <div className="flex gap-1 platform-toggle" onClick={(e) => e.stopPropagation()}>
                            {post.content?.facebook && (
                                <button 
                                    onClick={() => setViewPlatform('facebook')}
                                    className={`p-1 rounded-full transition-colors ${viewPlatform === 'facebook' ? 'text-blue-400 bg-blue-400/10' : 'text-cream/20 hover:text-cream/50'}`}
                                >
                                    <FiFacebook size={12} />
                                </button>
                            )}
                            {post.content?.instagram && (
                                <button 
                                    onClick={() => setViewPlatform('instagram')}
                                    className={`p-1 rounded-full transition-colors ${viewPlatform === 'instagram' ? 'text-pink-400 bg-pink-400/10' : 'text-cream/20 hover:text-cream/50'}`}
                                >
                                    <FiInstagram size={12} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                <p className="text-sm text-cream/70 line-clamp-2 font-medium leading-relaxed group-hover:text-cream transition-colors">
                    "{contentPreview}{contentPreview?.length >= 50 ? '...' : ''}"
                </p>
                {post.status === 'posted' && post.insights && (
                    <div className="flex items-center gap-4 mt-2 text-[10px] font-bold text-cream/40 uppercase tracking-widest">
                        <span className="flex items-center gap-1">
                            <FiEye size={10} /> {(post.insights.facebook?.views || 0) + (post.insights.instagram?.views || 0)}
                        </span>
                        <span className="flex items-center gap-1">
                            <FiHeart size={10} /> {(post.insights.facebook?.likes || 0) + (post.insights.instagram?.likes || 0)}
                        </span>
                        <span className="flex items-center gap-1">
                            <FiMessageCircle size={10} /> {(post.insights.facebook?.comments || 0) + (post.insights.instagram?.comments || 0)}
                        </span>
                    </div>
                )}
            </div>
            
            {/* Status Icon Column */}
            <div className={`mt-2 p-2 rounded-full border border-transparent flex-shrink-0 ${isHovered ? 'bg-white/5 text-slate-blue' : 'text-cream/10'}`}>
               <FiArrowRight />
            </div>
        </div>
    );
};

// --- Main Dashboard Component ---
function Dashboard() {
    const navigate = useNavigate();
    const userId = auth.currentUser?.uid;
    const userName = auth.currentUser?.displayName || auth.currentUser?.email?.split('@')[0] || 'Creator';

    // --- State ---
    const [projects, setProjects] = useState([]);
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    const [projectSearch, setProjectSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    

    // Schedule state
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [scheduledPosts, setScheduledPosts] = useState([]);
    const [loadingPosts, setLoadingPosts] = useState(false);
    const [selectedPost, setSelectedPost] = useState(null);
    const [hoveredPostId, setHoveredPostId] = useState(null);


    // Failed posts (date-independent, always shown in Needs Attention)
    const [failedPosts, setFailedPosts] = useState([]);

    // --- Compute Attention Items ---
    const attentionItems = useMemo(() => {
        const failures = [];
        const warnings = [];
        const opportunities = [];

        const relevantPosts = selectedProjectId ? scheduledPosts : [];

        // Failures: from dedicated failedPosts query (date-independent) + date-scoped posts with errors
        const failedIds = new Set();
        failedPosts.forEach(post => {
            failedIds.add(post.id);
            failures.push({
                type: 'failed',
                post,
                title: 'Post failed to publish',
                subtitle: (post.content?.facebook || post.content?.instagram || '').slice(0, 40) + '...'
            });
        });
        // Also catch posts stuck as "scheduled" but with a lastAttemptError (from old retry resets)
        relevantPosts.filter(p => !failedIds.has(p.id) && (p.status === 'failed' || p.lastAttemptError)).forEach(post => {
            failures.push({
                type: 'failed',
                post,
                title: 'Post failed to publish',
                subtitle: (post.content?.facebook || post.content?.instagram || '').slice(0, 40) + '...'
            });
        });

        // Warnings: scheduled posts missing visual for 24+ hours
        const oneDayAgo = new Date();
        oneDayAgo.setDate(oneDayAgo.getDate() - 1);
        relevantPosts.filter(p =>
            (p.visualStatus === 'queued' || p.visualStatus === 'pending') &&
            p.createdAt?.toDate?.() < oneDayAgo
        ).forEach(post => {
            warnings.push({
                type: 'missing_visual',
                post,
                title: 'Visual missing for 24+ hours',
                subtitle: (post.content?.facebook || post.content?.instagram || '').slice(0, 40) + '...'
            });
        });

        return { failures, warnings, opportunities, total: failures.length + warnings.length + opportunities.length };
    }, [scheduledPosts, failedPosts, selectedProjectId]);

    // --- Computed Values ---
    const selectedProject = useMemo(() => 
        projects.find(p => p.id === selectedProjectId), 
        [projects, selectedProjectId]
    );
    
    const filteredProjects = useMemo(() => {
        if (!projectSearch.trim()) return projects;
        const search = projectSearch.toLowerCase();
        return projects.filter(p => 
            p.projectName?.toLowerCase().includes(search) || 
            p.industry?.toLowerCase().includes(search)
        );
    }, [projects, projectSearch]);

    const isToday = useMemo(() => {
        const today = new Date();
        return selectedDate.toDateString() === today.toDateString();
    }, [selectedDate]);

    // --- Handlers ---
    const handleSelectProject = useCallback((projectId) => {
        // Updated: Only set local state, NO updateDoc to prevent sorting jumps
        setSelectedProjectId(projectId);
    }, []);

    const navigateWithProject = useCallback((path) => {
        if (selectedProjectId) {
            navigate(path.replace('{id}', selectedProjectId));
        }
    }, [selectedProjectId, navigate]);

    const changeDate = useCallback((delta) => {
        setSelectedDate(prev => {
            const newDate = new Date(prev);
            newDate.setDate(newDate.getDate() + delta);
            return newDate;
        });
    }, []);

    // --- Effect: Fetch Projects (Updated Sorting) ---
    useEffect(() => {
        if (!userId) {
            setError("Please log in.");
            setLoading(false);
            return;
        }
        
        setError('');
        setLoading(true);

        const q = query(
            collection(db, "projects"),
            where("userId", "==", userId),
            orderBy("updatedAt", "desc"), // Updated sort key
            limit(4) // Limit to top 4 as requested
        );

        let fallbackUnsub = null;

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const projectsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setProjects(projectsData);
            setLoading(false);
        }, (err) => {
            // Fallback if index missing
             const fallbackQ = query(
                collection(db, "projects"),
                where("userId", "==", userId),
                orderBy("createdAt", "desc"),
                limit(4) // Limit to top 4
            );
             fallbackUnsub = onSnapshot(fallbackQ, (snapshot) => {
                 const projectsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                 setProjects(projectsData);
                 setLoading(false);
             }, (fallbackErr) => {
                 setError("Could not load projects.");
                 setLoading(false);
             });
        });

        return () => { unsubscribe(); if (fallbackUnsub) fallbackUnsub(); };
    }, [userId]);

    // --- Effect: Auto-select First Project ---
    useEffect(() => {
        if (projects.length > 0 && !selectedProjectId) {
            setSelectedProjectId(projects[0].id);
        }
    }, [projects, selectedProjectId]);

    // --- Effect: Fetch Posts for Selected Project + Date ---
    // Two queries: (1) scheduled posts by scheduledTime, (2) posted posts by postedAt
    // This ensures immediately published posts (no scheduledTime) also appear.
    useEffect(() => {
        if (!selectedProjectId || !userId) {
            setScheduledPosts([]);
            return;
        }

        setLoadingPosts(true);

        const startOfDay = new Date(selectedDate);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(selectedDate);
        endOfDay.setHours(23, 59, 59, 999);

        const postsRef = collection(db, "projects", selectedProjectId, "posts");

        // Query 1: Posts with a scheduledTime in the selected day
        const q1 = query(
            postsRef,
            where("scheduledTime", ">=", Timestamp.fromDate(startOfDay)),
            where("scheduledTime", "<=", Timestamp.fromDate(endOfDay)),
            orderBy("scheduledTime", "asc")
        );

        // Query 2: Posts published today (by postedAt) — catches Deploy Now posts
        const q2 = query(
            postsRef,
            where("status", "==", "posted"),
            where("postedAt", ">=", Timestamp.fromDate(startOfDay)),
            where("postedAt", "<=", Timestamp.fromDate(endOfDay)),
            orderBy("postedAt", "asc")
        );

        const mergedPosts = new Map();
        let loadedCount = 0;

        const mergePosts = () => {
            const all = Array.from(mergedPosts.values());
            // Sort by display time: scheduledTime or postedAt
            all.sort((a, b) => {
                const timeA = a.scheduledTime?.toDate?.() || a.postedAt?.toDate?.() || new Date(0);
                const timeB = b.scheduledTime?.toDate?.() || b.postedAt?.toDate?.() || new Date(0);
                return timeA - timeB;
            });
            setScheduledPosts(all);
        };

        const unsub1 = onSnapshot(q1, (snapshot) => {
            snapshot.docs.forEach(doc => mergedPosts.set(doc.id, { id: doc.id, ...doc.data() }));
            loadedCount++;
            if (loadedCount >= 2) { mergePosts(); setLoadingPosts(false); }
        }, () => {
            loadedCount++;
            if (loadedCount >= 2) { mergePosts(); setLoadingPosts(false); }
        });

        const unsub2 = onSnapshot(q2, (snapshot) => {
            snapshot.docs.forEach(doc => mergedPosts.set(doc.id, { id: doc.id, ...doc.data() }));
            loadedCount++;
            if (loadedCount >= 2) { mergePosts(); setLoadingPosts(false); }
        }, () => {
            loadedCount++;
            if (loadedCount >= 2) { mergePosts(); setLoadingPosts(false); }
        });

        return () => { unsub1(); unsub2(); };
    }, [selectedProjectId, selectedDate, userId]);


    // --- Effect: Fetch failed posts (date-independent, always visible in Needs Attention) ---
    useEffect(() => {
        if (!selectedProjectId || !userId) {
            setFailedPosts([]);
            return;
        }

        const postsRef = collection(db, "projects", selectedProjectId, "posts");
        const q = query(
            postsRef,
            where("status", "==", "failed"),
            limit(10)
        );

        const unsub = onSnapshot(q, (snapshot) => {
            setFailedPosts(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        }, () => {
            // Silently ignore index/permission errors
        });

        return () => unsub();
    }, [selectedProjectId, userId]);

    // --- Render ---
    if (loading && projects.length === 0) {
        return (
            <div className="flex justify-center items-center h-screen bg-warm-dark">
                <div className="flex flex-col items-center gap-4">
                    <FiLoader className="animate-spin text-4xl text-slate-blue" />
                    <p className="text-cream/50 text-sm tracking-wider uppercase">Loading Dashboard...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-warm-dark text-cream selection:bg-slate-blue/30 pb-20">
            <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-12">
            
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-8 border-b border-white/5">
                    <div>
                         <div className="flex items-center gap-2 mb-2">
                             <span className="px-3 py-1 bg-slate-blue/10 rounded-full border border-slate-blue/20 text-[10px] uppercase font-black tracking-widest text-slate-blue">
                                 v{APP_VERSION}
                             </span>
                             <span className="text-[10px] text-cream/30 uppercase tracking-widest">{formatDate(new Date())}</span>
                         </div>
                        <h1 className="text-5xl font-black text-cream uppercase tracking-tighter drop-shadow-lg">
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-blue to-cream">Dashboard</span>
                        </h1>
                        <p className="text-cream/50 mt-2 font-medium text-lg">Welcome back, {userName}.</p>
                    </div>
                    <button
                        onClick={() => navigate('/create-project')}
                        className="btn btn-primary px-8 py-4 text-sm shadow-glow hover:shadow-glow/80 hover:scale-105 active:scale-95 transition-all"
                    >
                        <FiPlus className="mr-2 h-5 w-5" /> New Project
                    </button>
                </div>

                {error && <div className="p-6 bg-soft-coral/10 border border-soft-coral/20 text-soft-coral rounded-[2rem] text-sm font-bold flex items-center shadow-lg"><FiAlertCircle className="mr-3 h-5 w-5"/>{error}</div>}

                {/* Getting Started Checklist */}
                <GettingStarted projects={projects} userId={userId} />

                {/* Section 1: Project Cards */}
                <section className="bg-warm-gray/10 backdrop-blur-md rounded-[3rem] border border-white/5 p-10 shadow-2xl relative overflow-hidden group">
                     {/* Ambient Glow */}
                     {/* Ambient Glow - Removed per user feedback */}

                     <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10 relative z-10">
                        <h2 className="text-sm font-black text-cream/40 uppercase tracking-widest flex items-center">
                            <FiLayers className="mr-2 h-4 w-4 text-slate-blue" /> Your Projects
                        </h2>
                        <div className="flex items-center gap-4">
                            <div className="relative group/search">
                                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-cream/30 group-focus-within/search:text-slate-blue transition-colors" />
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={projectSearch}
                                    onChange={(e) => setProjectSearch(e.target.value)}
                                    className="pl-12 pr-6 py-3 text-sm bg-black/20 border border-white/5 rounded-full focus:ring-1 focus:ring-slate-blue focus:border-slate-blue text-cream w-56 placeholder-cream/20 transition-all shadow-inner focus:w-64 focus:bg-black/40"
                                />
                            </div>
                            <Link to="/projects" className="text-xs font-bold text-slate-blue hover:text-cream flex items-center whitespace-nowrap uppercase tracking-wide transition-colors bg-white/5 hover:bg-white/10 px-4 py-3 rounded-full border border-white/5">
                                View All <FiArrowRight className="ml-2 h-3 w-3" />
                            </Link>
                        </div>
                    </div>
                    
                    {projects.length === 0 ? (
                        <div className="text-center py-20 border-2 border-dashed border-white/5 rounded-[2rem] bg-black/10 flex flex-col items-center justify-center">
                            <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mb-6">
                                <FiGrid className="h-8 w-8 text-cream/20" />
                            </div>
                            <p className="mt-2 text-lg font-medium text-cream/40">No projects yet.</p>
                            <button
                                onClick={() => navigate('/create-project')}
                                className="mt-8 btn btn-secondary px-8 py-3 text-sm"
                            >
                                <FiPlus className="mr-2" /> Create Project
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
                            {filteredProjects.map(project => (
                                <SelectableProjectCard
                                    key={project.id}
                                    project={project}
                                    isSelected={project.id === selectedProjectId}
                                    onClick={() => handleSelectProject(project.id === selectedProjectId ? null : project.id)}
                                />
                            ))}
                        </div>
                    )}
                </section>

                {/* Main Content Grid: Quick Actions + Attention / Schedule */}
                {projects.length > 0 && <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    
                    {/* Left Column: Quick Actions + Attention Widget */}
                    <div className="lg:col-span-1 space-y-8">
                        
                        {/* Quick Actions */}
                        <section className="bg-warm-gray/10 backdrop-blur-md rounded-[3rem] border border-white/5 p-8 shadow-xl">
                            <h2 className="text-sm font-black text-cream/40 uppercase tracking-widest mb-8 flex items-center">
                                <FiZap className="mr-2 h-4 w-4 text-slate-blue" />
                                Quick Actions
                                {selectedProject && (
                                    <span className="ml-auto px-3 py-1 rounded-full bg-slate-blue/10 text-[9px] font-black text-slate-blue border border-slate-blue/20 truncate max-w-[120px] uppercase tracking-wider">
                                        {selectedProject.projectName}
                                    </span>
                                )}
                            </h2>
                            
                            <div className="space-y-4">
                                <QuickActionButton
                                    icon={<FiEye />}
                                    label="Project Overview"
                                    onClick={() => navigateWithProject('/project/{id}')}
                                    disabled={!selectedProjectId}
                                />
                                <QuickActionButton
                                    icon={<FiEdit2 />}
                                    label="Content Hub"
                                    onClick={() => navigateWithProject('/project/{id}/content-hub')}
                                    disabled={!selectedProjectId}
                                />
                                <QuickActionButton
                                    icon={<FiCalendar />}
                                    label="Calendar"
                                    onClick={() => navigate(`/calendar${selectedProjectId ? `?project=${selectedProjectId}` : ''}`)}
                                    disabled={!selectedProjectId}
                                />
                            </div>
                            
                            {/* System Idle Empty Project State */}
                        {!selectedProjectId && (
                            <div className="mt-6 p-6 bg-black/20 rounded-[2rem] border border-white/5 border-dashed text-center">
                                <p className="text-[10px] text-cream/40 font-bold uppercase tracking-widest">
                                    Select a project to enable actions
                                </p>
                            </div>
                        )}
                    </section>

                    {/* Attention Widget */}
                    <section className="bg-warm-gray/10 backdrop-blur-md rounded-[2.5rem] border border-white/5 p-8 shadow-xl">
                        <h2 className="text-sm font-black text-cream/40 uppercase tracking-widest mb-6 flex items-center">
                            <FiAlertCircle className="mr-2 h-4 w-4 text-soft-coral" />
                            Needs Attention
                        </h2>

                        {!selectedProjectId ? (
                            <div className="text-center py-8 bg-white/5 rounded-3xl border border-white/5">
                                <p className="text-sm text-cream/30 italic">Select a project to see alerts</p>
                            </div>
                        ) : attentionItems.total === 0 ? (
                            <div className="text-center py-8 bg-green-500/5 rounded-3xl border border-green-500/10">
                                <FiCheckCircle className="mx-auto h-10 w-10 text-pastel-green/50 mb-3" />
                                <p className="text-sm font-bold text-pastel-green uppercase tracking-wide">All Good</p>
                                <p className="text-xs text-cream/30 mt-1">Nothing needs your attention</p>
                            </div>
                        ) : (
                            <div className="space-y-5">
                                {/* Failures */}
                                {attentionItems.failures.length > 0 && (
                                    <div>
                                        <p className="text-[9px] font-black uppercase tracking-widest text-red-400/60 mb-2 px-1">Failures</p>
                                        <div className="space-y-2">
                                            {attentionItems.failures.slice(0, 3).map((item, idx) => (
                                                <AttentionItem
                                                    key={idx}
                                                    icon={<FiAlertCircle className="h-5 w-5 text-soft-coral" />}
                                                    iconColor="text-soft-coral"
                                                    bgColor="bg-soft-coral/5"
                                                    borderColor="border-soft-coral/20"
                                                    title={item.title}
                                                    subtitle={item.subtitle}
                                                    actions={
                                                        <button
                                                            onClick={() => setSelectedPost(item.post)}
                                                            className="text-[10px] font-black uppercase text-cream bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full transition-colors"
                                                        >
                                                            Retry
                                                        </button>
                                                    }
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Warnings */}
                                {attentionItems.warnings.length > 0 && (
                                    <div>
                                        <p className="text-[9px] font-black uppercase tracking-widest text-amber-400/60 mb-2 px-1">Warnings</p>
                                        <div className="space-y-2">
                                            {attentionItems.warnings.slice(0, 3).map((item, idx) => (
                                                <AttentionItem
                                                    key={idx}
                                                    icon={<FiImage className="h-5 w-5 text-amber-400" />}
                                                    iconColor="text-amber-400"
                                                    bgColor="bg-amber-400/5"
                                                    borderColor="border-amber-400/20"
                                                    title={item.title}
                                                    subtitle={item.subtitle}
                                                    actions={
                                                        <button
                                                            onClick={() => setSelectedPost(item.post)}
                                                            className="text-[10px] font-black uppercase text-cream bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full transition-colors"
                                                        >
                                                            Fix
                                                        </button>
                                                    }
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}

                            </div>
                        )}
                        </section>
                </div>

                    {/* Right Column: Today's Schedule */}
                    <section className="lg:col-span-2 bg-warm-gray/10 backdrop-blur-md rounded-[3rem] border border-white/5 p-10 h-full flex flex-col shadow-xl relative overflow-hidden">
                        {/* Bg Mesh */}
                         <div className="absolute bottom-0 right-0 w-full h-1/2 bg-gradient-to-t from-slate-blue/5 to-transparent pointer-events-none"></div>

                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10 relative z-10">
                            <h2 className="text-sm font-black text-cream/40 uppercase tracking-widest flex items-center">
                                <FiCalendar className="mr-2 h-4 w-4 text-slate-blue" />
                                Today's Schedule
                            </h2>
                            
                            {/* Date Navigation */}
                            <div className="flex items-center gap-2 bg-black/20 p-1.5 rounded-full border border-white/5">
                                <button
                                    onClick={() => changeDate(-1)}
                                    className="p-3 rounded-full hover:bg-white/10 text-cream/40 hover:text-white transition-colors"
                                >
                                    <FiChevronLeft className="h-4 w-4" />
                                </button>
                                <div className="flex items-center gap-3 px-6 py-2">
                                    <span className="text-sm font-bold text-cream uppercase tracking-wide">
                                        {formatDate(selectedDate)}
                                    </span>
                                    {isToday && (
                                        <div className="h-2 w-2 rounded-full bg-slate-blue shadow-glow-sm"></div>
                                    )}
                                </div>
                                <button
                                    onClick={() => changeDate(1)}
                                    className="p-3 rounded-full hover:bg-white/10 text-cream/40 hover:text-white transition-colors"
                                >
                                    <FiChevronRight className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                        {!selectedProjectId ? (
                            <div className="text-center py-20 flex-1 flex flex-col justify-center items-center bg-black/10 rounded-[2rem] border border-white/5 border-dashed">
                                <FiLayers className="mx-auto h-16 w-16 text-cream/5 mb-6 animate-pulse" />
                                <p className="text-xs text-cream/30 uppercase tracking-widest font-bold">Select a project to see schedule</p>
                            </div>
                        ) : loadingPosts ? (
                            <div className="text-center py-20 flex-1 flex flex-col justify-center items-center">
                                <FiLoader className="mx-auto h-10 w-10 text-slate-blue animate-spin" />
                            </div>
                        ) : scheduledPosts.length === 0 ? (
                            <div className="text-center py-16 bg-black/10 rounded-[2rem] border border-white/5 flex-1 flex flex-col justify-center items-center relative z-10">
                                <FiCalendar className="mx-auto h-14 w-14 text-cream/10 mb-6" />
                                <p className="text-lg font-bold text-cream/30 uppercase tracking-wide">Nothing scheduled</p>
                                <button
                                    onClick={() => navigateWithProject('/project/{id}/content-hub?workflow=single')}
                                    className="mt-8 btn btn-secondary px-8 py-3 text-xs shadow-none border border-white/10 hover:bg-white/10"
                                >
                                    <FiPlus className="mr-2" /> Schedule Post
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-6 flex-1 relative z-10">
                                {/* Timeline Bar */}
                            <div className="bg-warm-dark/50 rounded-2xl p-6 border border-white/5 shadow-inner mb-6">
                                <div className="flex justify-between text-[10px] text-cream/20 font-bold uppercase tracking-widest mb-4">
                                    <span>6AM</span>
                                    <span>9</span>
                                    <span>12PM</span>
                                    <span>3</span>
                                    <span>6</span>
                                    <span>9</span>
                                    <span>12AM</span>
                                </div>
                                <div className="relative h-2 bg-warm-gray/30 rounded-full overflow-visible">
                                     {scheduledPosts.map((post, idx) => {
                                        const displayTimestamp = post.scheduledTime || post.postedAt;
                                        const time = displayTimestamp?.toDate?.() || new Date();
                                        const hours = time.getHours();
                                        const minutes = time.getMinutes();
                                        const totalMinutes = (hours - 6) * 60 + minutes;
                                        const percentage = Math.max(0, Math.min(100, (totalMinutes / (18 * 60)) * 100)); // 6AM to 12AM = 18 hours

                                        return (
                                            <div
                                                key={idx}
                                                className="absolute w-4 h-4 rounded-full top-1/2 transform -translate-y-1/2 -translate-x-1/2 cursor-pointer transition-transform z-10 hover:scale-150 shadow-md group"
                                                style={{ left: `${percentage}%`, backgroundColor: post.visualStatus === 'ready' ? '#3B82F6' : '#78716c' }}
                                                title={formatTime(displayTimestamp)}
                                                onMouseEnter={() => setHoveredPostId(post.id)}
                                                onMouseLeave={() => setHoveredPostId(null)}
                                            >
                                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-warm-black text-cream text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                                                    {formatTime(displayTimestamp)}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Post List */}
                            <div className="space-y-4">
                                    {scheduledPosts.map(post => (
                                        <SchedulePostRow
                                            key={post.id}
                                            post={post}
                                            onExpand={setSelectedPost}
                                            isHovered={hoveredPostId === post.id}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </section>
                </div>}

                {/* Post Summary Modal */}
                {selectedPost && (
                    <PostSummaryModal
                        isOpen={!!selectedPost}
                        onClose={() => setSelectedPost(null)}
                        post={selectedPost}
                        project={selectedProject}
                    />
                )}
            </div>
        </div>
    );
}

export default Dashboard;
